/**
 * Frontend environment variables. Vite exposes anything prefixed with VITE_
 * on import.meta.env at build time.
 *
 * `websiteUrl` points at the public marketing site (the flowdesk-website
 * deployment). Used by the auth-screen copyright line and any other
 * "back to the brand site" affordance the panel surfaces. Falls back to
 * `https://taskmagix.com` so a dev build still produces a valid `<a href>`
 * even when the env-var is unset.
 */
export const env = {
  apiUrl: import.meta.env.VITE_API_URL ?? '',
  appName: import.meta.env.VITE_APP_NAME ?? 'TaskMagix',
  websiteUrl: import.meta.env.VITE_WEBSITE_URL ?? 'https://taskmagix.com',
}

/**
 * Which build is running — baked in by vite.config.js `define`. Surfaced on
 * the sign-in footer and as a `<meta name="app-build">` so a tester can
 * confirm the build under test without a session, and a bug report can be
 * tied to a deploy (QA-017).
 *
 * The `typeof` guard keeps `node --test` working: those files import app
 * modules outside a Vite transform, where the define does not exist.
 */
export const build =
  typeof __APP_BUILD__ !== 'undefined'
    ? __APP_BUILD__
    : { version: null, commit: null, builtAt: null }

/** "0.0.0+50e981e" — the shortest string that identifies a build. */
export function buildLabel() {
  const parts = [build.version ?? 'unversioned']
  if (build.commit) parts.push(build.commit)
  return parts.join('+')
}

/**
 * In dev the Vite proxy forwards /api/v1/* to the API, so axios can use
 * a relative baseURL. In prod builds (no dev proxy) we use VITE_API_URL.
 */
export function resolveApiBase() {
  if (import.meta.env.DEV) return '/api/v1'
  const base = env.apiUrl.replace(/\/$/, '')
  return base ? `${base}/api/v1` : '/api/v1'
}
